import PropTypes from 'prop-types';
import Pluralize from 'react-pluralize';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Paper } from '@material-ui/core';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import InfoBlock from '../../InfoBlock';
import AgentCommission from '../../TravelOption/HotelInformation/HotelItinerary/AgentCommission';

import useStyles from './styles';
import formatStateroomsInput from '../../../helpers/formatStateroomsInput';
import DistanceFromLocationLabel from '../../TravelOption/DistanceFromLocationLabel';
import HotelCancellationLabel from '../../TravelOption/HotelInformation/HotelCancellationLabel';
import RateDetailDropdownPopover from '../../TravelOption/HotelInformation/HotelItinerary/RateDetailDropdownPopover/index.tsx';
import { checkDoShowTripConfirmationPricing } from '../../../store/slices/user';
import RoomRateDescription from '../../TravelOption/HotelInformation/RoomRateDescription';
import checkDoShowMarkupRates from '../../../helpers/selectors/checkDoShowMarkupRates';
import { SearchModes, TravelTypes } from '../../../constants';
import getPaymentTypeLabel from '../../../helpers/getPaymentTypeLabel';
import Label from '../../Label';

dayjs.extend(dayjsPluginUTC);

const HotelInfo = ({ itinerary, selectedRoomCode, tripId, doShowAgentCommission }) => {
  const doShowPricing = useSelector(checkDoShowTripConfirmationPricing);
  const doShowMarkupRates = useSelector(
    checkDoShowMarkupRates({
      mode: TravelTypes.hotel,
      tripId,
      itineraryId: itinerary.id,
    }),
  );
  const classes = useStyles();
  const { staterooms } = useSelector((store) => store.tripSearch.searchObj);
  const { travelAgency } = useSelector((store) => store.user);
  const product = itinerary.hotel.products.find((item) => item.code === selectedRoomCode);

  return (
    <>
      <div className="grid grid-cols-4 gap-1">
        <div className="row-start-1 row-end-3">
          {itinerary.hotel.image ? (
            <img src={itinerary.hotel.image} alt="IMG" />
          ) : (
            <Paper square classes={classes}>
              No photo
            </Paper>
          )}
        </div>
        <div className="col-start-2 col-end-5 text-sm">{itinerary.hotel.name}</div>
        <div className="col-span-4 text-sm mt-1">
          <RoomRateDescription
            room={itinerary.hotel.products.find((item) => item.code === selectedRoomCode).rooms[0]}
            doShowAgentCommission={doShowAgentCommission}
            doShowRawGDSDescription={false}
            doShowOriginalShorterRoomDescription
          />
        </div>
        {product.isPrepaidRate ? (
          <div className="col-span-4 text-sm">
            <Label text="Prepaid rate" level="success" inline />
          </div>
        ) : (
          ''
        )}
        {product.paymentTypeCode != null && !product.isPrepaidRate && (
          <div className="col-span-4 text-sm">
            <Label
              text={getPaymentTypeLabel(product.paymentTypeCode, travelAgency)}
              level="warning"
              inline
            />
          </div>
        )}
      </div>
      {doShowAgentCommission && (
        <AgentCommission
          agentCommission={product.roomPrice.agentCommission}
          isCommissionable={product.roomPrice.isCommissionable}
          currencyCode={product.roomPrice.currencyCode}
          agentCommissionPercentage={product.roomPrice.agentCommissionPercentage}
          doShowCommissionAsPercentage={
            !!(
              travelAgency &&
              travelAgency.settings &&
              travelAgency.settings.doShowHotelComissionInPercentage
            )
          }
          className="whitespace-nowrap inline text-sm"
        />
      )}
      <InfoBlock title="Stay details">
        <div className="grid grid-cols-3 text-sm">
          <div className="font-bold">Check in:</div>
          <div className="col-span-2">
            {dayjs(itinerary.hotel.checkInStartTime).format('ddd D MMM, YYYY')}
          </div>
          <div className="font-bold">Check out:</div>
          <div className="col-span-2">
            {dayjs(itinerary.hotel.checkOutEndTime).format('ddd D MMM, YYYY')}
          </div>
          {itinerary.duration ? (
            <>
              <div className="font-bold">Duration:</div>
              <div className="col-span-2">
                <Pluralize singular="night" count={itinerary.duration} />
              </div>
            </>
          ) : (
            ''
          )}
          <div className="font-bold">Guests:</div>
          <div className="col-span-2">
            {formatStateroomsInput(
              staterooms.map((stateroom) => ({
                ...stateroom,
                children: [],
                seatInfants: 0,
                lapInfants: 0,
              })),
              travelAgency && travelAgency.enabledTransportModes,
              SearchModes.HOTEL,
            )}
          </div>
          <div className="font-bold">City:</div>
          <div className="col-span-2">{itinerary.hotel.city}</div>
          <div className="font-bold">Cancellation:</div>
          <div className="col-span-2">
            <HotelCancellationLabel product={product} />
          </div>
          <DistanceFromLocationLabel
            distance={
              (itinerary.searchGeoname || {}).departureCityId
                ? itinerary.driveDistanceToCruisePort
                : itinerary.driveDistanceToSearchDestination
            }
            walkingDistance={
              !(itinerary.searchGeoname || {}).departureCityId &&
              itinerary.walkDistanceToSearchDestination
            }
            label={`${(itinerary.searchGeoname || {}).name || ''} ${(itinerary.searchGeoname || {}).departureCityId ? 'port' : ''}`}
            headingLabel={`Distance${(itinerary.searchGeoname || {}).departureCityId ? ' from port' : ''}:`}
            labelClassName="font-bold"
            valueClassName="col-span-2"
          />
          <div>&nbsp;</div>
          {doShowPricing && itinerary.selectedProduct.roomPrice && (
            <div className="col-span-2">
              <RateDetailDropdownPopover
                roomPrice={itinerary.selectedProduct.roomPrice}
                doShowMarkupRates={doShowMarkupRates}
                numberOfNights={itinerary.duration}
                fontSize="text-sm"
                alignClass="justify-left"
                popoverAlignClass="right-0"
                tripId={tripId}
              />
            </div>
          )}
        </div>
      </InfoBlock>
    </>
  );
};

HotelInfo.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  selectedRoomCode: PropTypes.string.isRequired,
  tripId: PropTypes.string.isRequired,
  doShowAgentCommission: PropTypes.bool.isRequired,
};

export default HotelInfo;
